(ns practitest.frontend.et.events
  (:require
    [cljs.spec.alpha :as s]
    [re-frame.core :as re-frame]
    [clojure.pprint :refer [cl-format]]
    [clojure.string :as string]
    [day8.re-frame.http-fx]
    [ajax.core :as ajax]
    [practitest.frontend.et.db :as db]
    [practitest.frontend.common.utils :refer [index-by-field-value]]
    [practitest.frontend.common.format-utils :refer [format-markdown-attachment-url]]
    [practitest.frontend.common.re-frame :refer [conditional-dispatch]]
    [practitest.frontend.common-old-fe.logging :as log]
    [practitest.frontend.subs :as subs]
    [practitest.frontend.routing.backend-routes :as broutes]
    [practitest.frontend.common-old-fe.utils :refer [format-duration parse-duration]]))

;; ===========================================================================
;; constants

(def ^:const request-timeout 5000)
(def ^:const long-request-timeout 10000)
(def ^:const autosave-period 30000)

;; ===========================================================================
;; interceptors

(defn check-and-throw
  "throw an exception if db doesn't match the spec"
  [a-spec db]
  (when-not (s/valid? a-spec db)
    (throw (ex-info (str "spec check failed: " (s/explain-str a-spec db)) {}))))

;; Event handlers change state, that's their job. But what happens if there's a bug
;; which corrupts app state in some subtle way? This interceptor runs after
;; each event handler has finished, and it checks the app-db against a spec.
;; It helps us detect event handler bugs early.
(def check-spec-interceptor (re-frame/after (partial check-and-throw :et.db/db)))

;; chain of interceptors
(def common-interceptors
  [check-spec-interceptor
   re-frame/trim-v])

;; ===========================================================================
;; utils

(defn select-paths
  "selects only parts of the entity accoording to paths"
  [m paths]
  (let [[fields attrs] ((juxt filter remove) vector? paths)
        fields (map last fields)]
    (merge (select-keys m attrs)
           {:fields (select-keys (:fields m) fields)})))

(defn duration-changed? [et-state]
  (let [[_ current-duration _] (:running-time et-state)
        entity-duration (get-in et-state [:entity :duration])]
    (not= entity-duration current-duration)))

(defn update-entity-request [uri {:keys [entity] :as et-state}]
  (when (or (seq (:dirty-fields entity)) (duration-changed? et-state))
    {:method          :put
     :uri             uri
     :params          {:et (assoc
                             (select-paths entity
                                           (conj (:dirty-fields entity) :id))
                             :duration (if-let [[duration _ _] (:running-time et-state)]
                                         (.asSeconds duration)
                                         0)
                             :update_tester (boolean (:update-tester? et-state)))}
     :timeout         request-timeout
     :format          (ajax/json-request-format)
     :response-format (ajax/json-response-format {:keywords? true})
     :on-success      [:et/updated]
     :on-failure      [:et/bad-http-result]}))

(defn delete-entity-request [uri]
  {:method          :delete
   :uri             uri
   :timeout         request-timeout
   :format          (ajax/json-request-format)
   :response-format (ajax/json-response-format {:keywords? true})
   :on-success      [:et/deleted]
   :on-failure      [:et/bad-http-result]})

(defn delete-attachment-request [uri attachment-id]
  {:method          :delete
   :uri             uri
   :params          {:attachment_id attachment-id}
   :timeout         request-timeout
   :format          (ajax/json-request-format)
   :response-format (ajax/json-response-format {:keywords? true})
   :on-success      [:et/attachment-deleted]
   :on-failure      [:et/bad-http-result]})

(defn create-attachment-request [annotation-uuid db entity-id attachment]
  (let [project_id  (subs/get-current-project-id db)
        url (broutes/get-url "pattachments#multiupload" {:project_id project_id})]
    {:method :post
     :uri {:url url :uuid annotation-uuid :action "create_attachment"}
     ;real body is creating in :et/annotations-saved event
     :_body  {:entity-id entity-id :attachment attachment :attachable_type "EtAnnotation"}
     :progress-handler (fn [event]
                         (js/console.log "upload-attachment:progress" event))
     :response-format (ajax/json-response-format {:keywords? true})
     :on-success      [:et/attachment-created annotation-uuid (:uuid attachment)]
     :on-failure      [:et/bad-http-result]}))

(defn save-annotations-request [uri annotations db]
  (let [attachment-requests (->> annotations
                                 (filter #(contains? (:dirty-fields %) :attachments))
                                 (mapcat (fn [{:keys [id uuid attachments-to-delete attachments]}]
                                           (concat
                                             (map (partial delete-attachment-request
                                                           (str uri "/" id "/destroy_attachment"))
                                                  attachments-to-delete)
                                             (map (partial create-attachment-request uuid db id)
                                                  (remove :id attachments))))))
        annotations (->> annotations
                         (map (fn [annotation]
                                (-> annotation
                                    (update :dirty-fields disj :attachments)
                                    (dissoc :attachments-to-delete))))
                         (filter #(seq (:dirty-fields %))))]
    (log/debug "save-annotations-request" attachment-requests annotations)
    {:method          :post
     :uri             (str uri "/save")
     :params          {:data (map #(select-keys % (conj (:dirty-fields %) :id)) annotations)}
     :timeout         request-timeout
     :format          (ajax/json-request-format)
     :response-format (ajax/json-response-format {:keywords? true})
     :on-success      [:et/annotations-saved (map :uuid annotations) attachment-requests]
     :on-failure      [:et/bad-http-result]}))

(defn delete-annotations-request [uri ids]
  {:method          :post
   :uri             uri
   :params          {:data ids}
   :timeout         request-timeout
   :format          (ajax/json-request-format)
   :response-format (ajax/json-response-format {:keywords? true})
   :on-success      [:et/annotations-deleted]
   :on-failure      [:et/bad-http-result]})

(defn calculate-changes* [db]
  (let [entity (:entity (:et-state db))
        uri-entity (str (db/uri-prefix (:et-state db)) "/et/" (:id entity))
        dirty-annotations (->> (:annotations entity)
                               (map-indexed #(assoc %2 :position %1))
                               (filter #(seq (:dirty-fields %))))]
    (cond-> []
      (or (seq (:dirty-fields entity)) (duration-changed? (:et-state db)))
      (conj (update-entity-request uri-entity (:et-state db)))

      (seq dirty-annotations)
      (conj (save-annotations-request (str uri-entity "/a") dirty-annotations db))

      (seq (:annotations-to-delete entity))
      (conj (delete-annotations-request (str uri-entity "/a/delete") (:annotations-to-delete entity))))))

(defn calculate-changes [db options]
  (log/debug "calculate-changes" (:et-state db) options)
  (if (get-in db [:et-state :entity :id])
    (calculate-changes* db)
    [{:method          :post
      :uri             (str (db/uri-prefix (:et-state db)) "/et")
      :params          {:et (select-keys (:entity (:et-state db)) [:title :fields :charter :guide-points])}
      :timeout         request-timeout
      :format          (ajax/json-request-format)
      :response-format (ajax/json-response-format {:keywords? true})

      :on-success      [:et/created (:action options)]
      :on-failure      [:et/bad-http-result]}]))

(defn pre-process-annotations [{:keys [hash]} annotations]
  (let [selected-annotation-id (when-not (string/blank? hash)
                                 (some->> hash
                                          (re-find #"^#(\d+)$")
                                          last
                                          js/parseInt))]
    (->> annotations
         (map (fn [annotation]
                (-> annotation
                    (assoc :uuid (random-uuid)
                           :focus (= selected-annotation-id (:id annotation))
                           :initial-focus (= selected-annotation-id (:id annotation)))
                    (update :attachments
                            (fn [attachments]
                              (->> attachments
                                   (map #(assoc % :uuid (random-uuid)))
                                   vec))))))
         vec)))

;; ===========================================================================
;; event handlers
(defn pre-add-annotation-attachment [ev]
  (log/info "pre-add-annotation-attachment: " ev)
  (let [detail (-> ev .-detail)
        annotation-position (.-position detail)
        file (.-file detail)
        file-data (js->clj file :keywordize-keys true)]
    (re-frame/dispatch [:et/add-annotation-attachment annotation-position file-data])))

(re-frame/reg-cofx
  :document/location
  (fn [cofx]
    (assoc cofx :location js/document.location)))

(re-frame/reg-event-fx
  :et/initialize
  [(re-frame/inject-cofx :document/location) common-interceptors]
  (fn [{:keys [db location] :as cofx} [options]]
    (log/info :et/initialize options)
    (let [entity (some-> (or (:entity db) (:entity options))
                         (update :annotations (partial pre-process-annotations location)))
          new-db (-> db
                     (assoc-in [:et-state] options)
                     (assoc-in [:et-state :entity] entity))
          requests (cond-> []
                     (nil? (:fields new-db))
                     (conj {:method          :get
                            :uri             (str (db/uri-prefix options) "/et/fields")
                            :timeout         request-timeout
                            :vec-strategy    :rails
                            :format          (ajax/url-request-format :rails)
                            :response-format (ajax/json-response-format {:keywords? true})
                            :on-success      [:et/fields-received]
                            :on-failure      [:et/bad-http-result]})

                     (nil? (:users new-db))
                     (conj {:method          :get
                            :uri             (:users-uri options)
                            :timeout         request-timeout
                            :vec-strategy    :rails
                            :format          (ajax/url-request-format :rails)
                            :response-format (ajax/json-response-format {:keywords? true})
                            :on-success      [:et/users-received]
                            :on-failure      [:et/bad-http-result]})
                     (and (nil? (get-in new-db [:entity :id])) (:entity-id options))
                     (conj {:method          :get
                            :uri             (str (db/uri-prefix options) "/et/" (:entity-id options))
                            :timeout         request-timeout
                            :vec-strategy    :rails
                            :format          (ajax/url-request-format :rails)
                            :response-format (ajax/json-response-format {:keywords? true})
                            :on-success      [:et/entity-received]
                            :on-failure      [:et/bad-http-result]}))
          running (if (and (not (:entity (:et-state db)))
                           (or (:id entity) (:entity-id options))
                           (:time-management options))
                    {:running-timer (js/setInterval #(re-frame/dispatch [:et/update-running]) 1000)}
                    (select-keys (:et-state db) [:running-timer :running-time]))
          autosave (if (and (not (:entity (:et-state db)))
                            (or (:id entity) (:entity-id options)))
                     {:autosave-timer (js/setInterval #(re-frame/dispatch [:et/auto-update])
                                                      autosave-period)}
                     (select-keys (:et-state db) [:autosave-timer]))]
      ;; it's not a good idea to do side effects like this in event handlers
      ;; will move it when i figure out where to put this thing
      (when (and (:time-management options) (not (:entity (:et-state db))))
        ;; this means that it's the first time initialize was called
        (.addEventListener js/document "attach-file" pre-add-annotation-attachment)
        (.addEventListener js/document "keypress" #(re-frame/dispatch [:et/reset-idle-timeout]))
        (.addEventListener js/document "mousedown" #(re-frame/dispatch [:et/reset-idle-timeout])))
      {:db         (-> new-db
                       (update-in [:et-state] merge running autosave))
       :http-xhrio requests})))

(re-frame/reg-event-db
  :et/init-complete
  (fn [db _]
    (assoc-in db [:et-state :init-complete?] true)))

(re-frame/reg-event-fx
  :et/update-running
  [check-spec-interceptor re-frame/trim-v]
  (fn [{:keys [db]} _]
    (let [et-state (:et-state db)
          [duration _ idle-duration] (:running-time et-state)
          duration (if duration (.add duration 1 "s") (js/moment.duration))
          duration-s (format-duration duration)
          idle-duration (if idle-duration (.add idle-duration 1 "s") (js/moment.duration))]
      (if (> (.asMinutes idle-duration) (:timer-idle-time et-state))
        {:db       (assoc db
                          :et-state (assoc et-state
                                           :running-time [duration duration-s (js/moment.duration)]
                                           :idle-message "Running Time has been paused due to long inactivity time."))
         :dispatch [:et/pause-running-timer]}
        {:db (assoc db
                    :et-state (assoc et-state
                                     :running-time [duration duration-s idle-duration]))}))))

(re-frame/reg-event-db
  :et/reset-idle-timeout
  [check-spec-interceptor re-frame/trim-v]
  (fn [db _]
    (assoc-in db [:et-state :running-time 2] (js/moment.duration))))

(re-frame/reg-event-fx
  :et/auto-update
  [check-spec-interceptor re-frame/trim-v]
  (fn [{:keys [db]} _]
    (if (or (seq (:dirty-fields (:entity (:et-state db))))
            (duration-changed? (:et-state db)))
      {:dispatch [:et/update]}
      {})))

(re-frame/reg-event-db
  :et/pause-running-timer
  common-interceptors
  (fn [db _]
    (let [timer (:running-timer (:et-state db))]
      (when timer
        (js/clearInterval timer))
      (update db :et-state dissoc :running-timer))))

(re-frame/reg-event-db
  :et/start-running-timer
  common-interceptors
  (fn [db _]
    (if-let [timer (:running-timer (:et-state db))]
      (do
        (log/warn :et/start-running-timer "already running")
        db)
      (let [timer (js/setInterval #(re-frame/dispatch [:et/update-running]) 1000)]
        (-> db
            (update :et-state dissoc :idle-message)
            (assoc-in [:et-state :running-timer] timer))))))

(re-frame/reg-event-db
  :et/fields-received
  common-interceptors
  (fn [db [fields]]
    (assoc-in db [:et-state :fields] fields)))

(re-frame/reg-event-db
  :et/users-received
  common-interceptors
  (fn [db [users]]
    (assoc-in db [:et-state :users] users)))

(re-frame/reg-event-db
  :et/entity-received
  common-interceptors
  (fn [db [entity]]
    (cond-> (assoc-in db [:et-state :entity]
                      (update entity :annotations
                              (fn [annotations]
                                (let [selected-annotation-id (when js/window.location.hash
                                                               (js/parseInt (last (re-find #"^#(\d+)$"
                                                                                           js/window.location.hash))))]
                                  (vec
                                    (map (fn [annotation]
                                           (-> annotation
                                               (assoc :uuid (random-uuid)
                                                      :focus (= selected-annotation-id (:id annotation))
                                                      :initial-focus (= selected-annotation-id (:id annotation))
                                                      :filter-display-flag true)
                                               (update :attachments
                                                       (fn [attachments]
                                                         (vec
                                                           (map #(assoc % :uuid (random-uuid))
                                                                attachments))))))
                                         annotations))))))
      (:running-timer (:et-state db)) (assoc-in [:et-state :running-time] [(parse-duration (:duration entity))
                                                                           (:duration entity)
                                                                           (js/moment.duration)]))))

(re-frame/reg-event-db
  :et/bad-http-result
  common-interceptors
  (fn [db [error]]
    (log/debug ":et/bad-http-result" (:response error))
    (-> db
        (update-in [:et-state] dissoc :loading?)
        (assoc-in [:et-state :errors] (get-in error [:response :errors])))))

(re-frame/reg-event-fx
  :et/create
  common-interceptors
  (fn [{:keys [db]} [action]]
    (let [requests (calculate-changes db {:action action})]
      (log/debug ":et/create" requests)
      {:db (assoc-in db [:et-state :loading?] true) :http-xhrio requests})))

(re-frame/reg-event-fx
  :et/update
  common-interceptors
  (fn [{:keys [db]} _]
    (let [requests (calculate-changes db {})
          new-db (-> db
                     (update-in [:et-state :entity]
                                (fn [entity]
                                  (-> entity
                                      (assoc :dirty-fields #{})
                                      (assoc :annotations-to-delete #{})
                                      (update :annotations
                                              (fn [annotations]
                                                (vec
                                                  (map (fn [annotation]
                                                         (assoc annotation
                                                                :dirty-fields #{}
                                                                :attachments-to-delete #{}))
                                                       annotations)))))))
                     (update-in [:et-state] dissoc :update-tester?))]
      (log/info ":et/update" requests)
      {:db         (assoc-in new-db [:et-state :loading?] true)
       :http-xhrio requests})))

(re-frame/reg-event-fx
  :et/delete
  common-interceptors
  (fn [{:keys [db]} _]
    {:http-xhrio (delete-entity-request (str (db/uri-prefix (:et-state db)) "/et/" (get-in db [:et-state :entity :instance-id])))}))

(re-frame/reg-event-fx
  :et/created
  common-interceptors
  (fn [{:keys [db]} [action data]]
    (log/debug ":et/created" data action)
    (if (= :close action)
      (.replace js/window.location
                (str (db/uri-prefix (:et-state db)) "/edit"))
      (.pushState js/window.history "" ""
                  (str (db/uri-prefix (:et-state db)) "/et/" (:instance-id data) "/edit")))
    {:db             (cond-> db
                       true (update-in [:et-state] dissoc :loading? :errors)
                       true (assoc-in [:et-state :entity] data)
                       true (assoc-in [:et-state :entity-id] (:id data))
                       true (assoc-in [:et-state :success-message] "Exploratory Test created successfully")
                       (:time-management (:et-state db)) (assoc-in [:et-state :running-timer] (js/setInterval #(re-frame/dispatch [:et/update-running]) 1000))
                       (:time-management (:et-state db)) (assoc-in [:et-state :running-time] [(parse-duration (:duration data))
                                                                                              (:duration data)
                                                                                              (js/moment.duration)]))
     :dispatch-later [{:ms 1000 :dispatch [:et/clear-success-message]}]}))

(re-frame/reg-event-fx
  :et/updated
  common-interceptors
  (fn [{:keys [db]} [data]]
    (log/info ":et/updated" data)
    {:db             (-> db
                         (assoc-in [:et-state :success-message] "Exploratory Test updated successfully")
                         (assoc-in [:et-state :entity :duration] (format-duration (parse-duration (:duration data))))
                         (assoc-in [:et-state :entity :last-modified] (:last-modified data))
                         (assoc-in [:et-state :entity :tester-id] (:tester-id data))
                         (update-in [:et-state :entity :annotations]
                                    (fn [annotations]
                                      (let [issues-map (->> (:annotations data)
                                                            (map (juxt :id :issues))
                                                            (into {}))]
                                        (vec
                                          (map (fn [a]
                                                 (if-let [issues (get issues-map (:id a))]
                                                   (assoc a :issues issues)
                                                   a))
                                               annotations)))))
                         (update-in [:et-state] dissoc :loading? :errors))
     :dispatch-later [{:ms 1000 :dispatch [:et/clear-success-message]}]}))

(re-frame/reg-event-db
  :et/clear-success-message
  common-interceptors
  (fn [db _]
    (assoc-in db [:et-state :success-message] nil)))

(re-frame/reg-event-db
  :et/deleted
  common-interceptors
  (fn [db [data]]
    (log/debug :et/deleted data)
    (.replace js/window.location (str (db/uri-prefix (:et-state db)) "/edit"))
    db))

(re-frame/reg-event-fx
  :et/annotations-saved
  common-interceptors
  (fn [{:keys [db]} [uuids attachment-requests entities]]
    (log/info ":et/annotations-saved" uuids attachment-requests entities)
    (let [ids (into {} (map vector uuids entities))
          annotations (vec
                        (map (fn [annotation]
                               (if (:id annotation)
                                 annotation
                                 (assoc annotation
                                        :id (get-in ids [(:uuid annotation) :id])
                                        :uris (get-in ids [(:uuid annotation) :uris]))))
                             (get-in db [:et-state :entity :annotations])))
          attachment-requests (map (fn [req]
                                     (if (and (map? (:uri req)) (= (-> req :uri :action) "create_attachment"))
                                       (let [{:keys [url uuid]} (:uri req)
                                             {:keys [entity-id attachment attachable_type]} (:_body req)
                                             id (if entity-id
                                                  entity-id
                                                  (get-in ids [uuid :id]))]
                                         (-> req
                                             (assoc :uri url
                                                    :body (doto (js/FormData.)
                                                            (.append "attachable_id" id)
                                                            (.append "attachable_type" attachable_type)
                                                            (.append "pattachments[]" (:file attachment) (:name attachment))))
                                             (dissoc :_body)))
                                       req))
                                   attachment-requests)]
      {:db         (-> db
                       (update-in [:et-state] dissoc :loading? :errors)
                       (assoc-in [:et-state :entity :annotations] annotations))
       :http-xhrio attachment-requests})))

(re-frame/reg-event-fx
  :et/attachment-created
  common-interceptors
  (fn [{:keys [db]} [annotation-uuid attachment-uuid saved-attachments]]
    (let [annotation-position (index-by-field-value (get-in db [:et-state :entity :annotations]) :uuid annotation-uuid)
          attachment-position (index-by-field-value (get-in db [:et-state :entity :annotations annotation-position :attachments]) :uuid attachment-uuid)

          annotation (get-in db [:et-state :entity :annotations annotation-position])
          attachment (get-in annotation [:attachments attachment-position])
          description-file-placeholder (:description-file-placeholder attachment)
          saved-attachment (-> saved-attachments :data last) ;; We upload only one file per request, so can take just last there
          ;; TODO: Possibly we should update ET components to use new attachment data format
          ;; We need this additional transformation to get the same format as we get from old create_attachment endpoint.
          saved-attachment (assoc saved-attachment :size (:file_size saved-attachment) :type (:file_type saved-attachment))]

      (-> {:db
           (-> (if description-file-placeholder
                 (let [current-description (:description annotation)
                       new-description (string/replace current-description description-file-placeholder (format-markdown-attachment-url saved-attachment))]
                   (-> db
                       (update-in [:et-state :entity :annotations annotation-position] assoc :description new-description)
                       (update-in [:et-state :entity :annotations annotation-position] assoc :dirty-fields #{:description})
                       (update-in [:et-state :entity :annotations annotation-position :attachments attachment-position] dissoc :description-file-placeholder)))
                 db)
               (update-in [:et-state :entity :annotations]
                          (fn [annotations]
                            (vec
                              (map (fn [annotation]
                                     (if (= annotation-uuid (:uuid annotation))
                                       (update annotation :attachments
                                               (fn [attachments]
                                                 (vec
                                                   (map (fn [attachment]
                                                          (if (= attachment-uuid (:uuid attachment))
                                                            (assoc saved-attachment :uuid attachment-uuid)
                                                            attachment))
                                                        attachments))))
                                       annotation))
                                   annotations)))))}
         ;; because we can only get a new-description after the attachment is uploaded to the server
         ;; in the case where a description-file-placeholder exists, we call update et test again to save the new-description in the annotation
          (conditional-dispatch (when description-file-placeholder [:et/update]))))))

(re-frame/reg-event-db
  :et/attachment-deleted
  common-interceptors
  (fn [db [entity]]
    (log/debug :et/attachment-deleted entity)
    db))

(re-frame/reg-event-db
  :et/annotations-deleted
  common-interceptors
  (fn [db [entities]]
    (log/debug ":et/annotations-deleted" entities)
    (update-in db [:et-state] dissoc :loading? :errors)))

(re-frame/reg-event-fx
  :et/add-annotation
  common-interceptors
  (fn [{:keys [db]} [position]]
    (cond-> {:db (db/add-annotation db [position])}
      (and (= "NO RUN" (get-in db [:et-state :entity :run-status]))
           (empty? (get-in db [:et-state :entity :annotations])))
      (assoc :dispatch [:et/set-run-status "NOT COMPLETED"])
      (empty? (get-in db [:et-state :entity :annotations]))
      (assoc-in [:db :et-state :update-tester?] true))))


(re-frame/reg-event-db :et/set-field common-interceptors
                       db/set-field)

(re-frame/reg-event-db :et/set-charter common-interceptors
                       db/set-charter)

(re-frame/reg-event-db :et/set-guide-points common-interceptors
                       db/set-guide-points)

(re-frame/reg-event-db :et/set-estimate common-interceptors
                       db/set-estimate)

(re-frame/reg-event-db
  :et/set-description-filter
  [check-spec-interceptor re-frame/trim-v]
  (fn [db [value]]
    (assoc-in db [:et-state :description-filter] value)))

(re-frame/reg-event-db
  :et/set-type-filter
  [check-spec-interceptor re-frame/trim-v]
  (fn [db [value]]
    (assoc-in db [:et-state :type-filter] value)))

(re-frame/reg-event-db
  :et/set-display-filters
  [check-spec-interceptor re-frame/trim-v]
  (fn [db [value]]
    (assoc-in db [:et-state :display-filters] value)))

(re-frame/reg-event-db
  :et/change-annotations-filter-flag
  [check-spec-interceptor re-frame/trim-v]
  (fn [db _]
    (db/change-annotations-filter-flag db)))

(re-frame/reg-event-db :et/set-run-status common-interceptors
                       db/set-run-status)

(re-frame/reg-event-db
  :et/set-running-time
  common-interceptors
  (fn [db [value]]
    (log/info :et/set-running-time value)
    (let [running-time (if (string? value)
                         (parse-duration value)
                         value)]
      (assoc-in db [:et-state :running-time] [running-time (format-duration running-time)]))))

(re-frame/reg-event-db :et/set-annotation-type common-interceptors
                       db/set-annotation-type)

(re-frame/reg-event-db :et/set-annotation-description common-interceptors
                       db/set-annotation-description)

(re-frame/reg-event-db
  :et/set-annotation-focus
  common-interceptors
  (fn [db [position]]
    (update-in db [:et-state :entity :annotations]
               (fn [annotations]
                 (vec
                   (map-indexed (fn [i annotation]
                                  (assoc annotation :focus (= position i)))
                                annotations))))))

(re-frame/reg-event-db :et/move-annotation-down common-interceptors
                       db/move-annotation-down)

(re-frame/reg-event-db :et/move-annotation-up common-interceptors
                       db/move-annotation-up)

(re-frame/reg-event-db :et/remove-annotation common-interceptors
                       db/remove-annotation)

(re-frame/reg-event-db :et/add-annotation-attachment common-interceptors
                       db/add-annotation-attachment)

(re-frame/reg-event-db :et/remove-annotation-attachment common-interceptors
                       db/remove-annotation-attachment)

(re-frame/reg-event-fx
  :et/remove-annotation-issue
  common-interceptors
  (fn [{:keys [db]} [position issue]]
    {:db         (update-in db [:et-state :entity :annotations position :issues]
                            (fn [issues]
                              (vec (remove #(= (:id issue) (:id %)) issues))))
     :http-xhrio {:method          :delete
                  :uri             (:delete-url issue)
                  :timeout         request-timeout
                  :format          (ajax/json-request-format)
                  :response-format (ajax/json-response-format {:keywords? true})
                  :on-success      [:et/annotation-issue-deleted position (:id issue)]
                  :on-failure      [:et/bad-http-result]}}))

(re-frame/reg-event-db
  :et/annotation-issue-deleted
  common-interceptors
  (fn [db [position issue-id result]]
    (log/info :et/annotation-issue-deleted result)
    db))

(re-frame/reg-event-fx
  :et/refresh-linked-issues
  common-interceptors
  (fn [{:keys [db]} [annotation-position]]
    (let [annotation (get-in db [:et-state :entity :annotations annotation-position])]
      {:db         (update-in db [:et-state :refreshing-linked-issues]
                              (fn [x]
                                (set (conj x (:id annotation)))))
       :http-xhrio {:method          :get
                    :uri             (get-in annotation [:uris :refresh-linked-issues])
                    :timeout         request-timeout
                    :vec-strategy    :rails
                    :format          (ajax/url-request-format :rails)
                    :response-format (ajax/json-response-format {:keywords? true})
                    :on-success      [:et/annotation-linked-issues-received]
                    :on-failure      [:et/bad-http-result]}})))

(re-frame/reg-event-db
  :et/annotation-linked-issues-received
  common-interceptors
  (fn [db [result]]
    (-> db
        (update-in [:et-state :refreshing-linked-issues] disj (:id result))
        (update-in [:et-state :entity :annotations]
                   (fn [annotations]
                     (vec
                       (map (fn [annotation]
                              (if (= (:id result) (:id annotation))
                                (assoc annotation :issues (:issues result))
                                annotation))
                            annotations)))))))

(re-frame/reg-event-db
  :et/start-linking-existing
  common-interceptors
  (fn [db [id]]
    (log/info :start-linking-existing id)
    (update-in db [:et-state :linking-existing] assoc id {})))

(re-frame/reg-event-db
  :et/cancel-linking-existing
  common-interceptors
  (fn [db [id]]
    (update-in db [:et-state :linking-existing] dissoc id)))

(re-frame/reg-event-fx
  :et/link-existing
  common-interceptors
  (fn [{:keys [db]} [position v]]
    (let [annotation (get-in db [:et-state :entity :annotations position])
          uri (get-in annotation [:uris :link-existing-issue])]
      (log/info :et/link-existing uri v)
      {:http-xhrio {:method          :post
                    :uri             uri
                    :params          {:issue_display_id v}
                    :format          (ajax/json-request-format)
                    :response-format (ajax/json-response-format {:keywords? true})
                    :on-success      [:et/linked-existing (:id annotation) position]
                    :on-failure      [:error-linking-issue (:id annotation)]}})))

(re-frame/reg-event-fx
  :et/linked-existing
  common-interceptors
  (fn [{:keys [db]} [id position response]]
    (log/info :et/linked-existing id position response)
    (if (and (empty? (:errors response))
             (empty? (:messages response)))
      {:db       (update db [:et-state :linking-existing] dissoc id)
       :dispatch [:et/refresh-linked-issues position]}
      {:db (update-in db [:et-state :linking-existing id]
                      assoc :errors (:errors response) :messages (:messages response))})))

(re-frame/reg-event-fx
  :et/run-next-test
  common-interceptors
  (fn [_ [url]]
    (log/info :et/run-next-test url)
    {:http-xhrio {:method          :post
                  :uri             url
                  :format          (ajax/json-request-format)
                  :params          {:run_from_instance (when @(re-frame/subscribe [::subs/new-run-form-enabled?]) "true")}
                  :response-format (ajax/json-response-format {:keywords? true})
                  :on-success      [:et/next-test-run]
                  :on-failure      [:et/bad-http-result]}}))

(re-frame/reg-event-db
  :et/next-test-run
  common-interceptors
  (fn [db [result]]
    (log/info :et/next-test-run result)
    (if (:redirect result)
      (do (set! (.-location js/window) (:redirect result))
          db)
      (-> db
          (update-in [:et-state] dissoc :loading?)
          (assoc-in [:et-state :errors] (:errors result))))))
